import React, { FC, useEffect, useRef, useState } from "react";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
// Relative, not "@/…": react-scripts does not resolve tsconfig paths for runtime imports
// (SIMS-9066 found `yarn build` broken by exactly that).
import {
	AMOUNT_PLACEHOLDER,
	firstBlankMessageOption,
	optionsMissingAmountPlaceholder,
	prizeMessageOptions,
	prizeTierMessageHasTypedCurrency,
	prizeTierPays,
	withMessageOptions,
} from "../../utils/fishbowlPrizeMessage";

/**
 * SIMS-9080 (PRD v0.3 FR-5/FR-6/FR-7, decisions D3/D5) — the message OPTIONS of one prize tier.
 *
 * Replaces the single "Display Message" field. The list is the tier's options in display order;
 * each row shows its reference number, which is the row's POSITION at render and is never stored
 * (D5: a stored number would point at a different sentence after a reorder). Removing option 2 of
 * 3 therefore leaves "1." and "2." (AC2) with no bookkeeping at all.
 *
 * Writes go through withMessageOptions() (portal D-95): one option is stored the legacy way
 * (`displayMessage` only), two or more as `displayMessages` with `displayMessage` = option 1. A
 * pre-release tier opens as one row and, untouched, is never rewritten (AC5).
 *
 * Reorder is ↑/↓ buttons — the product's existing pattern (CompoundConfigSection) and operable
 * without a pointer (D-101). Focus follows the option that moved; a polite live region says what
 * happened, so the change is not silent to assistive technology.
 *
 * Rendered whatever the draw presentation is (AC3, FR-6): the parent mounts it under the Digital
 * Fishbowl section, not under a presentation branch.
 *
 * ERROR TIMING (manual review, 2026-10-21). An option ADDED here shows its errors (blank, missing
 * [AMOUNT]) only once its field has been left: a row the configurator has just asked for is not yet
 * wrong. Options that were already on the tier when the editor opened show theirs at once. That
 * also covers "on save": the plan's save gate runs after "Update Template" has closed this editor,
 * so reopening it after a refused save shows every option's error. The helper paragraph is
 * rendered once by the parent, under "Prize Distributions" (MESSAGE_OPTIONS_HELP).
 *
 * Test ids follow portal Q-2 / D-103. Synthetic data only.
 */

/** The tier fields this editor reads and writes; anything else on the tier is carried through. */
export interface MessageOptionsTier {
	amount?: unknown;
	probability?: unknown;
	displayMessage?: unknown;
	displayMessages?: unknown;
	[key: string]: unknown;
}

interface MessageOptionsEditorProps {
	/** 0-based position of the tier — the key in every test id (a tier has no stable record id). */
	tierIndex: number;
	tier: MessageOptionsTier;
	onChange: (tier: MessageOptionsTier) => void;
}

export const MESSAGE_OPTIONS_HELP =
	"Each draw on a tier shows a different option until all have been used, then they " +
	"repeat — so more options means more variety, up to the number of draws a participant can " +
	"earn. Participants see this text: don't include names, identifiers or clinical details.";

const DEFAULT_HELPER =
	"Use [AMOUNT] — it shows the amount with its currency symbol";
const TYPED_CURRENCY_WARNING =
	"[AMOUNT] already includes the currency symbol — remove the one before it, or participants " +
	"will see it twice.";

type FocusTarget = { key: string; part: "field" | "up" | "down" } | null;

let nextKey = 0;
const newKey = (): string => `message-option-${(nextKey += 1)}`;

/** Visually hidden but read by screen readers (the usual clip pattern). */
const visuallyHidden = {
	position: "absolute",
	width: 1,
	height: 1,
	padding: 0,
	margin: -1,
	overflow: "hidden",
	clip: "rect(0 0 0 0)",
	whiteSpace: "nowrap",
	border: 0,
} as const;

const MessageOptionsEditor: FC<MessageOptionsEditorProps> = ({
	tierIndex,
	tier,
	onChange,
}) => {
	const options = prizeMessageOptions(tier);
	// A tier with no message at all still shows one (empty) row: there is always an option to type
	// into, and "remove" is disabled at one, so a tier never has zero options.
	const rows = options.length > 0 ? options : [""];

	// Client-side row keys, so React keeps each row's DOM (and focus) with its option across a
	// reorder instead of with its index. Never stored.
	const keysRef = useRef<string[]>([]);
	while (keysRef.current.length < rows.length) keysRef.current.push(newKey());
	if (keysRef.current.length > rows.length)
		keysRef.current = keysRef.current.slice(0, rows.length);
	const keys = keysRef.current;

	const fieldRefs = useRef<Record<string, HTMLInputElement | null>>({});
	const upRefs = useRef<Record<string, HTMLButtonElement | null>>({});
	const downRefs = useRef<Record<string, HTMLButtonElement | null>>({});
	const [focusTarget, setFocusTarget] = useState<FocusTarget>(null);
	const [announcement, setAnnouncement] = useState("");
	// Keys of options added in this editor whose field has not been left yet: their errors wait.
	const [untouched, setUntouched] = useState<ReadonlySet<string>>(new Set());
	const markLeft = (key: string): void => {
		if (!untouched.has(key)) return;
		const next = new Set(untouched);
		next.delete(key);
		setUntouched(next);
	};

	useEffect(() => {
		if (!focusTarget) return;
		const { key, part } = focusTarget;
		const target =
			part === "field"
				? fieldRefs.current[key]
				: part === "up"
					? upRefs.current[key]
					: downRefs.current[key];
		target?.focus();
		setFocusTarget(null);
	}, [focusTarget]);

	const t = tierIndex + 1;
	const id = (suffix: string): string =>
		`fishbowl-prize-tier-${tierIndex}-${suffix}`;

	const write = (next: string[]): void => {
		onChange(withMessageOptions(tier, next) as MessageOptionsTier);
	};

	const editOption = (j: number, value: string): void => {
		const next = [...rows];
		next[j] = value;
		write(next);
	};

	const addOption = (): void => {
		const key = newKey();
		keysRef.current = [...keys, key];
		setUntouched(new Set(untouched).add(key));
		write([...rows, ""]);
		setAnnouncement(`Option ${rows.length + 1} added`);
		setFocusTarget({ key, part: "field" });
	};

	const removeOption = (j: number): void => {
		if (rows.length <= 1) return;
		const nextKeys = keys.filter((_, i) => i !== j);
		keysRef.current = nextKeys;
		write(rows.filter((_, i) => i !== j));
		setAnnouncement(`Option ${j + 1} removed`);
		// The option now at that position, else the new last one.
		const focusKey = nextKeys[Math.min(j, nextKeys.length - 1)];
		setFocusTarget({ key: focusKey, part: "field" });
	};

	const moveOption = (j: number, delta: -1 | 1): void => {
		const to = j + delta;
		if (to < 0 || to >= rows.length) return;
		const next = [...rows];
		[next[j], next[to]] = [next[to], next[j]];
		const nextKeys = [...keys];
		[nextKeys[j], nextKeys[to]] = [nextKeys[to], nextKeys[j]];
		keysRef.current = nextKeys;
		write(next);
		setAnnouncement(`Option ${j + 1} moved to position ${to + 1}`);
		// Focus stays with the option that moved: the same button, or the opposite one when the
		// move has disabled it (top or bottom of the list).
		const atEdge = delta === -1 ? to === 0 : to === rows.length - 1;
		const same = delta === -1 ? "up" : "down";
		const opposite = delta === -1 ? "down" : "up";
		setFocusTarget({ key: nextKeys[to], part: atEdge ? opposite : same });
	};

	const pays = prizeTierPays(tier);
	const missing = pays ? optionsMissingAmountPlaceholder(rows) : [];
	const blank = firstBlankMessageOption(rows);

	const errorFor = (j: number): string | null => {
		if (untouched.has(keys[j])) return null;
		if (rows.length > 1 && rows[j].trim().length === 0) {
			return "Write this option or remove it";
		}
		if (missing.includes(j)) {
			return rows.length > 1
				? `This tier pays more than zero, so every option must include ${AMOUNT_PLACEHOLDER}`
				: `This tier pays more than zero, so its message must include ${AMOUNT_PLACEHOLDER}`;
		}
		return null;
	};

	return (
		<Box>
			<Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
				Message options
			</Typography>
			<Box
				component="ul"
				data-testid={id("messages")}
				aria-label={`Message options for prize tier ${t}`}
				data-blank-option={blank >= 0 ? String(blank) : undefined}
				sx={{ listStyle: "none", p: 0, m: 0 }}
			>
				{rows.map((option, j) => {
					const key = keys[j];
					const error = errorFor(j);
					const typedCurrency =
						!error &&
						prizeTierMessageHasTypedCurrency({ displayMessage: option });
					return (
						<Box
							component="li"
							key={key}
							data-testid={id(`message-row-${j}`)}
							sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}
						>
							<Typography
								variant="body2"
								data-testid={id(`message-${j}-number`)}
								sx={{ minWidth: 24, pt: 1 }}
							>
								{`${j + 1}.`}
							</Typography>
							<TextField
								fullWidth
								size="small"
								label={`Option ${j + 1}`}
								value={option}
								inputRef={(el: HTMLInputElement | null) => {
									fieldRefs.current[key] = el;
								}}
								inputProps={{
									"data-testid": id(`message-${j}`),
								}}
								onChange={(e) => editOption(j, e.target.value)}
								onBlur={() => markLeft(key)}
								placeholder={`e.g., Great! You earned ${AMOUNT_PLACEHOLDER} or Better luck next time!`}
								error={Boolean(error)}
								helperText={
									error ? (
										<Box
											component="span"
											data-testid={id(`message-${j}-error`)}
										>
											{error}
										</Box>
									) : typedCurrency ? (
										// A warning, not an error: the save is NOT blocked (Q-16 / OQ-2).
										<Box
											component="span"
											sx={{ color: "warning.main" }}
											data-testid={id(`message-${j}-warning`)}
										>
											{TYPED_CURRENCY_WARNING}
										</Box>
									) : (
										DEFAULT_HELPER
									)
								}
							/>
							<IconButton
								size="small"
								ref={(el: HTMLButtonElement | null) => {
									upRefs.current[key] = el;
								}}
								data-testid={id(`message-${j}-move-up`)}
								aria-label={`Move option ${j + 1} of prize tier ${t} up`}
								disabled={j === 0}
								onClick={() => moveOption(j, -1)}
							>
								<ArrowUpwardIcon fontSize="small" />
							</IconButton>
							<IconButton
								size="small"
								ref={(el: HTMLButtonElement | null) => {
									downRefs.current[key] = el;
								}}
								data-testid={id(`message-${j}-move-down`)}
								aria-label={`Move option ${j + 1} of prize tier ${t} down`}
								disabled={j === rows.length - 1}
								onClick={() => moveOption(j, 1)}
							>
								<ArrowDownwardIcon fontSize="small" />
							</IconButton>
							<IconButton
								size="small"
								data-testid={id(`message-${j}-remove`)}
								aria-label={`Remove option ${j + 1} of prize tier ${t}`}
								disabled={rows.length <= 1}
								onClick={() => removeOption(j)}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Box>
					);
				})}
			</Box>
			<Button
				size="small"
				startIcon={<AddIcon />}
				data-testid={id("message-add")}
				onClick={addOption}
			>
				Add message option
			</Button>
			<Box
				aria-live="polite"
				data-testid={id("messages-live")}
				sx={visuallyHidden}
			>
				{announcement}
			</Box>
		</Box>
	);
};

export default MessageOptionsEditor;
